import React, { useState, useMemo } from 'react';
import {
  BookOpen, Plus, Search, MoreHorizontal, FileText, Link as LinkIcon, Upload,
  Clock, CheckCircle, AlertTriangle, ExternalLink, Trash2, Edit2, Eye,
  RefreshCw, Zap, Quote, ArrowRight, Copy, Database, Globe, File
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';

const sourceTypes = {
  document: { label: 'Document', icon: FileText, color: 'text-blue-600' },
  url: { label: 'URL', icon: Globe, color: 'text-green-600' },
  confluence: { label: 'Confluence', icon: Database, color: 'text-blue-500' },
  notion: { label: 'Notion', icon: File, color: 'text-slate-600' }
};

const statusConfig = {
  indexed: { label: 'Indexed', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: RefreshCw },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  stale: { label: 'Stale', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Clock }
};

const mockSources = [
  {
    id: 'src-1',
    name: 'IT Runbook - Password Procedures',
    type: 'document',
    url: null,
    status: 'indexed',
    chunks: 45,
    lastIndexed: '2024-01-15T08:00:00Z',
    citationCount: 128,
    addedBy: 'Emily Zhang',
    tags: ['password', 'security', 'procedures']
  },
  {
    id: 'src-2',
    name: 'Okta Admin Guide',
    type: 'url',
    url: 'https://help.okta.com/admin-guide',
    status: 'indexed',
    chunks: 312,
    lastIndexed: '2024-01-14T12:00:00Z',
    citationCount: 89,
    addedBy: 'David Park',
    tags: ['okta', 'identity', 'sso']
  },
  {
    id: 'src-3',
    name: 'Engineering Wiki - Access Policies',
    type: 'confluence',
    url: 'https://company.atlassian.net/wiki/spaces/ENG/pages/access-policies',
    status: 'indexed',
    chunks: 78,
    lastIndexed: '2024-01-15T06:00:00Z',
    citationCount: 56,
    addedBy: 'Alex Kim',
    tags: ['access', 'policies', 'engineering']
  },
  {
    id: 'src-4',
    name: 'Hardware Procurement Policy',
    type: 'document',
    url: null,
    status: 'stale',
    chunks: 23,
    lastIndexed: '2023-12-01T10:00:00Z',
    citationCount: 34,
    addedBy: 'Taylor Brown',
    tags: ['hardware', 'procurement', 'budget']
  },
  {
    id: 'src-5',
    name: 'Security Incident Playbook',
    type: 'notion',
    url: 'https://notion.so/company/security-playbook',
    status: 'processing',
    chunks: null,
    lastIndexed: null,
    citationCount: 0,
    addedBy: 'Alex Kim',
    tags: ['security', 'incident', 'playbook']
  },
  {
    id: 'src-6',
    name: 'VPN Troubleshooting Guide',
    type: 'url',
    url: 'https://internal.company.com/vpn-guide',
    status: 'failed',
    chunks: null,
    lastIndexed: null,
    citationCount: 0,
    addedBy: 'Jordan Lee',
    tags: ['vpn', 'network', 'troubleshooting'],
    error: 'Failed to fetch URL: 403 Forbidden'
  }
];

const mockCitations = [
  {
    id: 'cite-1',
    source: 'IT Runbook - Password Procedures',
    excerpt: 'For admin accounts, always require security team approval before processing password reset...',
    usedIn: 'TKT-1235',
    timestamp: '2024-01-15T10:32:00Z'
  },
  {
    id: 'cite-2',
    source: 'Okta Admin Guide',
    excerpt: 'To reset a user password via API, use the /api/v1/users/{userId}/lifecycle/reset_password endpoint...',
    usedIn: 'TKT-1235',
    timestamp: '2024-01-15T10:32:00Z'
  },
  {
    id: 'cite-3',
    source: 'Engineering Wiki - Access Policies',
    excerpt: 'GitHub organization membership requires manager approval for all non-engineering roles...',
    usedIn: 'TKT-1239',
    timestamp: '2024-01-15T09:15:00Z'
  }
];

export const KnowledgeBase = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [activeTab, setActiveTab] = useState('sources');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCitationDialog, setShowCitationDialog] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState(null);

  const filteredSources = useMemo(() => {
    return mockSources.filter(src => {
      const matchesSearch = src.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        src.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesType = selectedType === 'all' || src.type === selectedType;
      return matchesSearch && matchesType;
    });
  }, [searchQuery, selectedType]);

  const stats = {
    total: mockSources.length,
    indexed: mockSources.filter(s => s.status === 'indexed').length,
    totalChunks: mockSources.reduce((sum, s) => sum + (s.chunks || 0), 0),
    citationsToday: mockCitations.length
  };

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Knowledge</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage knowledge sources and view AI citations
              </p>
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Source
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Knowledge Source</DialogTitle>
                  <DialogDescription>
                    Add documents or URLs for the AI to reference when answering questions
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Source Type</Label>
                    <Select defaultValue="document">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="document">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Upload Document
                          </div>
                        </SelectItem>
                        <SelectItem value="url">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            Web URL
                          </div>
                        </SelectItem>
                        <SelectItem value="confluence">
                          <div className="flex items-center gap-2">
                            <Database className="w-4 h-4" />
                            Confluence Page
                          </div>
                        </SelectItem>
                        <SelectItem value="notion">
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4" />
                            Notion Page
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input placeholder="e.g., VPN Setup Guide" />
                  </div>
                  <div className="space-y-2">
                    <Label>URL or File</Label>
                    <div className="flex gap-2">
                      <Input placeholder="https://... or drop file" className="flex-1" />
                      <Button variant="outline">
                        <Upload className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Tags (comma-separated)</Label>
                    <Input placeholder="e.g., vpn, network, setup" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                  <Button onClick={() => setShowAddDialog(false)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Source
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Sources</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.indexed}</p>
                  <p className="text-xs text-muted-foreground">Indexed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalChunks}</p>
                  <p className="text-xs text-muted-foreground">Knowledge Chunks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Quote className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.citationsToday}</p>
                  <p className="text-xs text-muted-foreground">Citations Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="sources">Knowledge Sources</TabsTrigger>
              <TabsTrigger value="citations">Recent Citations</TabsTrigger>
            </TabsList>

            {activeTab === 'sources' && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search sources..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(sourceTypes).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="sources" className="space-y-4">
            {filteredSources.map((source) => {
              const type = sourceTypes[source.type];
              const status = statusConfig[source.status];
              const TypeIcon = type.icon;
              const StatusIcon = status.icon;

              return (
                <Card key={source.id} className={cn(source.status === 'failed' && "border-red-200")}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={cn("w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0")}>
                        <TypeIcon className={cn("w-5 h-5", type.color)} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-foreground">{source.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {type.label}
                          </Badge>
                          <Badge variant="outline" className={cn("text-xs", status.color)}>
                            <StatusIcon className={cn("w-3 h-3 mr-1", source.status === 'processing' && "animate-spin")} />
                            {status.label}
                          </Badge>
                        </div>

                        {source.url && (
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            {source.url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}

                        {source.error && (
                          <p className="text-sm text-red-600 mt-1">{source.error}</p>
                        )}

                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          {source.chunks && (
                            <span className="flex items-center gap-1">
                              <Database className="w-3 h-3" />
                              {source.chunks} chunks
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Quote className="w-3 h-3" />
                            {source.citationCount} citations
                          </span>
                          {source.lastIndexed && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Indexed {new Date(source.lastIndexed).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-1">
                          {source.tags.map((tag, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {source.status === 'stale' && (
                          <Button variant="outline" size="sm">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Re-index
                          </Button>
                        )}
                        {source.status === 'failed' && (
                          <Button variant="outline" size="sm">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Retry
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              Preview Content
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Re-index Now
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          <TabsContent value="citations">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Quote className="w-5 h-5 text-primary" />
                  Recent Citations
                </CardTitle>
                <CardDescription>
                  Knowledge excerpts used by AI when answering requests
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {mockCitations.map((citation) => (
                  <div
                    key={citation.id}
                    className="p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedCitation(citation);
                      setShowCitationDialog(true);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="text-xs">
                            <BookOpen className="w-3 h-3 mr-1" />
                            {citation.source}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Used in {citation.usedIn}
                          </span>
                        </div>
                        <p className="text-sm text-foreground italic">"{citation.excerpt}"</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 ml-4" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="mt-6 border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Turn Citations into Actions</p>
                    <p className="text-sm text-blue-700 mt-1">
                      When viewing a ticket, you can click on any citation to see the full source context.
                      If the knowledge suggests an action, you can convert it directly into a workflow step.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showCitationDialog} onOpenChange={setShowCitationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Citation Detail</DialogTitle>
            <DialogDescription>
              Knowledge used to answer request
            </DialogDescription>
          </DialogHeader>
          {selectedCitation && (
            <div className="py-4 space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Source</Label>
                <p className="text-sm font-medium text-foreground">{selectedCitation.source}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Used In</Label>
                <Badge variant="outline">{selectedCitation.usedIn}</Badge>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Excerpt</Label>
                <div className="mt-2 p-4 bg-muted rounded-lg">
                  <p className="text-sm text-foreground italic">"{selectedCitation.excerpt}"</p>
                </div>
              </div>
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm text-foreground">
                  <strong>Turn into action:</strong> This citation suggests a specific procedure.
                  You can create a workflow step based on this knowledge.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCitationDialog(false)}>Close</Button>
            <Button variant="outline">
              <Copy className="w-4 h-4 mr-2" />
              Copy Excerpt
            </Button>
            <Button>
              <Zap className="w-4 h-4 mr-2" />
              Create Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBase;
